import { api } from './client';
import type { OrganizationKind } from './organizations';

export interface ActLine {
  id: string;
  lineNumber: number;
  nomenclatureId: string | null;
  name: string;
  article: string | null;
  unitId: string | null;
  unitCode: string | null;
  unitSymbol: string | null;
  quantity: string;
  price: string;
  vatRateCode: string;
  vatRateValue: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
}

export interface ActLineInput {
  nomenclatureId?: string | null;
  name: string;
  article?: string | null;
  unitId?: string | null;
  quantity: string;
  price: string;
  vatRateId: string;
}

export interface ActOrganizationRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  addressFull: string | null;
  phone: string | null;
  email: string | null;
  directorPosition: string | null;
  directorName: string | null;
  accountantName: string | null;
  accountantIsDirector: boolean;
}

export interface ActCounterpartyRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
  actualAddressFull: string | null;
}

export interface ActContractRef {
  id: string;
  number: string;
  date: string;
}

export interface ActInvoiceRef {
  id: string;
  number: string;
  date: string;
}

export interface Act {
  id: string;
  organizationId: string;
  organization: ActOrganizationRef;
  counterpartyId: string;
  counterparty: ActCounterpartyRef;
  contractId: string | null;
  contract: ActContractRef | null;
  invoiceId: string | null;
  invoice: ActInvoiceRef | null;
  number: string;
  numberInt: number;
  date: string;
  basis: string | null;
  periodFrom: string | null;
  periodTo: string | null;
  signerPosition: string | null;
  signerName: string | null;
  subtotal: string;
  vatAmount: string;
  total: string;
  comment: string | null;
  footerText: string | null;
  lines: ActLine[];
}

/** Поля шапки, общие для создания и редактирования. */
interface ActBody {
  date: string;
  basis?: string | null;
  periodFrom?: string | null;
  periodTo?: string | null;
  signerPosition?: string | null;
  signerName?: string | null;
  comment?: string | null;
  footerText?: string | null;
  lines: ActLineInput[];
}

export interface ActCreateInput extends ActBody {
  organizationId: string;
  counterpartyId: string;
  contractId?: string | null;
}

export type ActUpdateInput = ActBody;

export interface ActListItem {
  id: string;
  organizationId: string;
  organization: { id: string; shortName: string };
  counterpartyId: string;
  counterparty: { id: string; shortName: string };
  invoice: ActInvoiceRef | null;
  number: string;
  date: string;
  total: string;
}

export interface ActListResponse {
  items: ActListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ActListParams {
  search?: string;
  organizationId?: string;
  counterpartyId?: string;
  invoiceId?: string;
  page?: number;
  pageSize?: number;
}

function buildQuery(params: ActListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.counterpartyId) search.set('counterpartyId', params.counterpartyId);
  if (params.invoiceId) search.set('invoiceId', params.invoiceId);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const actsApi = {
  list: (params: ActListParams = {}) => api.get<ActListResponse>(`/acts${buildQuery(params)}`),

  get: (id: string) => api.get<{ act: Act }>(`/acts/${id}`),

  create: (data: ActCreateInput) => api.post<{ act: Act }>('/acts', data),

  createFromInvoice: (invoiceId: string) => api.post<{ act: Act }>(`/acts/from-invoice/${invoiceId}`),

  update: (id: string, data: ActUpdateInput) => api.patch<{ act: Act }>(`/acts/${id}`, data),

  remove: (id: string) => api.delete<void>(`/acts/${id}`),
};
